<template>
  <div>
    <div class="flex items-center mb-3">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Documents
      </h3>
      <hr class="flex-1 border-primary mx-4" />
      <AppButton
          color="green"
          label="Add Document"
          @click="addNewDocument"
          :icon="true"
          class="mr-2"
      >
        <template v-slot:icon>
          <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </template>
      </AppButton>
    </div>

    <div class="mb-4">
      <div class="flex items-center">
        <span class="text-sm text-secondary font-semibold mr-3">View:</span>

        <div class="inline-flex items-center bg-gray-100 rounded-lg p-1 space-x-1">
          <button
              type="button"
              :class="segBtnClass(documentsView === 'attached')"
              @click="setDocumentsView('attached')"
          >
            Attached Existing
          </button>

          <button
              type="button"
              :class="segBtnClass(documentsView === 'uploaded')"
              @click="setDocumentsView('uploaded')"
          >
            Uploaded New
          </button>
        </div>
      </div>
    </div>

    <div>
      <DataTable :value="documents" dataKey="document_id" :paginator="true" :rows="10" :lazy="false">
        <Column field="name" header="Name" :headerStyle="{ width: '30%' }"></Column>

        <Column field="original_filename" header="FileName" :headerStyle="{ width: '25%' }">
          <template #body="slotProps">
            <div class="flex items-center gap-2">
              <i :class="fileIconClass(slotProps.data)" class="text-gray-600 text-sm"></i>
              <span class="truncate" :title="slotProps.data.original_filename">
                {{ slotProps.data.original_filename }}
              </span>
            </div>
          </template>
        </Column>

        <Column field="description" header="Description" :headerStyle="{ width: '35%' }"></Column>

        <Column header="" :headerStyle="{ width: '10%' }">
          <template #body="slotProps">
            <div class="flex justify-center space-x-2">
              <AppButton color="orange" label="" :icon="true" @click="viewDoc(slotProps.data)">
                <template v-slot:icon>
                  <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </template>
              </AppButton>

              <AppButton color="green" label="" :icon="true" @click="downloadDoc(slotProps.data)">
                <template v-slot:icon>
                  <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </template>
              </AppButton>

              <AppButton
                  v-if="canEdit(slotProps.data)"
                  color="blue"
                  label=""
                  :icon="true"
                  @click="editDoc(slotProps.data)"
              >
                <template v-slot:icon>
                  <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                  >
                    <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </template>
              </AppButton>

              <AppButton
                  v-if="canDelete(slotProps.data)"
                  color="red"
                  label=""
                  :icon="true"
                  @click="deleteDocument(slotProps.data)"
              >
                <template v-slot:icon>
                  <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </template>
              </AppButton>

              <AppButton
                  v-if="canDetach(slotProps.data)"
                  color="red"
                  label=""
                  :icon="true"
                  @click="detachDocument(slotProps.data)"
              >
                <template v-slot:icon>
                  <Icon iconType="circle-minus" />
                </template>
              </AppButton>
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="w-full text-center text-gray-500 py-4">
            No documents available.
          </div>
        </template>
      </DataTable>
    </div>

    <portal to="overlay-outlet">
      <panel :showing="addDocumentPanelOpen" @close="onAddPanelClose" title="Add Document">
        <AddDocument :product_id="product_id" mode="add" @complete="handleAddComplete" />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel :showing="editDocumentPanelOpen" @close="onEditPanelClose" title="Edit Document">
        <AddDocument
            :product_id="product_id"
            mode="edit"
            :document="editingDocument"
            @complete="handleEditComplete"
        />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <ViewDocument
          :showing="viewingDocument.panelOpen"
          :document="viewingDocument.doc"
          @close="viewingDocument.panelOpen = false"
      />
    </portal>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import ViewDocument from "@/components/ViewDocument.vue";
import Panel from "@/components/Panel.vue";
import AddDocument from "@/components/ProductsVerified/Documents/AddDocument.vue";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  name: "DocumentList",
  mixins: [notificationMixin],
  components: { AppButton, Icon, ViewDocument, Panel, AddDocument },
  props: {
    product_id: { type: String, required: true },
  },
  data() {
    return {
      documents: [],
      documentsView: "attached",
      addDocumentPanelOpen: false,
      editDocumentPanelOpen: false,
      editingDocument: null,
      viewingDocument: {
        doc: null,
        panelOpen: false,
      },
    };
  },
  async mounted() {
    await this.fetchDocuments();
  },
  methods: {
    segBtnClass(selected) {
      return [
        "px-3 py-1 text-sm rounded-md transition duration-200 focus:outline-none",
        selected
            ? "bg-white shadow border border-gray-200 text-secondary font-semibold"
            : "bg-transparent text-gray-600 hover:bg-white hover:shadow-sm",
      ].join(" ");
    },

    setDocumentsView(view) {
      this.documentsView = view;
      this.fetchDocuments();
    },

    getFileExtensionFromName(filename) {
      if (!filename) return "";
      const parts = String(filename).split(".");
      if (parts.length < 2) return "";
      return String(parts.pop()).toLowerCase();
    },

    getDocumentTypeKey(document) {
      const filename =
          (document && document.original_filename && String(document.original_filename)) ||
          (document && document.filename && String(document.filename)) ||
          "";

      const ext = this.getFileExtensionFromName(filename);

      if (ext === "pdf") return "pdf";
      if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "webp" || ext === "gif" || ext === "bmp" || ext === "svg")
        return "image";
      if (ext === "doc" || ext === "docx") return "word";
      if (ext === "xls" || ext === "xlsx") return "excel";
      if (ext === "txt" || ext === "rtf" || ext === "csv") return "text";
      if (ext === "zip" || ext === "rar" || ext === "7z") return "zip";

      return "file";
    },

    fileIconClass(document) {
      const key = this.getDocumentTypeKey(document);
      if (key === "pdf") return "pi pi-file-pdf";
      if (key === "image") return "pi pi-image";
      if (key === "word") return "pi pi-file-word";
      if (key === "excel") return "pi pi-file-excel";
      if (key === "text") return "pi pi-file";
      if (key === "zip") return "pi pi-file";
      return "pi pi-file";
    },

    async fetchDocuments() {
      try {
        const docRole = "document";
        let response = [];

        if (this.documentsView === "uploaded") {
          response = await this.ProductServiceV2.getProductDocuments(
              this.product_id,
              docRole,
              undefined,
              { resourceRole: "document", uploadedNewOnly: true }
          );
        } else {
          response = await this.ProductServiceV2.getProductDocuments(
              this.product_id,
              docRole,
              undefined,
              { resourceRole: "document", attachedExistingOnly: true }
          );
        }

        this.documents = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error fetching documents:", error);
        this.notifyError("Unable to fetch documents.");
      }
    },

    addNewDocument() {
      this.addDocumentPanelOpen = true;
    },
    onAddPanelClose() {
      this.addDocumentPanelOpen = false;
    },

    editDoc(document) {
      this.editingDocument = Object.assign({}, document);
      this.editDocumentPanelOpen = true;
    },
    onEditPanelClose() {
      this.editDocumentPanelOpen = false;
      this.editingDocument = null;
    },

    getResourceTagForCurrentProduct(document, expectedRole) {
      let tags = [];
      if (document && Array.isArray(document.resource_tags)) tags = document.resource_tags;

      const pid = this.product_id ? String(this.product_id) : "";
      const role = expectedRole ? String(expectedRole).toLowerCase() : "";

      for (let i = 0; i < tags.length; i++) {
        const t = tags[i];
        if (!t) continue;

        const rid = t.resource_id === undefined || t.resource_id === null ? "" : String(t.resource_id);
        const rtype = t.resource_type ? String(t.resource_type).toLowerCase() : "";
        const rrole = t.resource_role ? String(t.resource_role).toLowerCase() : "";

        if (rid === pid && rtype === "product") {
          if (role) {
            if (rrole === role) return t;
          } else {
            return t;
          }
        }
      }

      return null;
    },

    canEdit(document) {
      if (this.documentsView !== "uploaded") return false;

      const tag = this.getResourceTagForCurrentProduct(document, "document");
      if (!tag) return true;

      return tag.is_attached_existing === false || tag.is_attached_existing === undefined || tag.is_attached_existing === null;
    },

    canDetach(document) {
      if (this.documentsView !== "attached") return false;

      const tag = this.getResourceTagForCurrentProduct(document, "document");
      if (!tag) return true;

      return tag.is_attached_existing === true || tag.is_attached_existing === undefined || tag.is_attached_existing === null;
    },

    canDelete(document) {
      if (this.documentsView !== "uploaded") return false;

      const tag = this.getResourceTagForCurrentProduct(document, "document");
      if (!tag) return true;

      return tag.is_attached_existing === false || tag.is_attached_existing === undefined || tag.is_attached_existing === null;
    },

    async viewDoc(document) {
      try {
        if (!document || !document.document_id) {
          this.notifyError("Invalid document.");
          return;
        }

        const doc = await this.DocumentService.getDocumentById(document.document_id);
        this.viewingDocument = { doc: doc, panelOpen: true };
      } catch (error) {
        console.error("Error viewing document:", error);
        this.notifyError("Unable to open document.");
      }
    },

    async downloadDoc(document) {
      try {
        if (!document || !document.document_id) return;

        const doc = await this.DocumentService.getDocumentById(document.document_id);
        if (doc && doc.access_url) window.open(doc.access_url);
      } catch (error) {
        console.log(error);
      }
    },

    async detachDocument(document) {
      try {
        if (!this.canDetach(document)) {
          this.notifyWarning("This document cannot be detached.");
          return;
        }

        const confirmation = await this.confirmAction({
          message: `Do you want to detach the document "${(document && document.name) || "this document"}"?`,
          header: "Confirm Action",
          acceptLabel: "Yes",
          rejectLabel: "No",
        });

        if (!confirmation) return;

        const tag = this.getResourceTagForCurrentProduct(document, "document");
        if (!tag || !tag.tag_id) {
          this.notifyError("Resource not found for the document.");
          return;
        }

        await this.ProductServiceV2.detachProductDocument(
            this.product_id,
            document.document_id,
            tag.tag_id
        );

        this.notifySuccess("Document detached successfully.");
        await this.fetchDocuments();
      } catch (error) {
        console.error("Error detaching document:", error);
        this.notifyError("Unable to detach document.");
      }
    },

    async deleteDocument(document) {
      try {
        if (!this.canDelete(document)) {
          this.notifyWarning("This document cannot be deleted.");
          return;
        }

        const confirmation = await this.confirmAction({
          message: `Do you want to delete the document "${(document && document.name) || "this document"}"?`,
          header: "Confirm Action",
          acceptLabel: "Yes",
          rejectLabel: "No",
        });

        if (!confirmation) return;

        if (this.ProductServiceV2 && typeof this.ProductServiceV2.deleteProductDocument === "function") {
          await this.ProductServiceV2.deleteProductDocument(this.product_id, document.document_id);
        } else if (this.DocumentService && typeof this.DocumentService.deleteDocument === "function") {
          await this.DocumentService.deleteDocument(document.document_id);
        } else if (this.DocumentService && typeof this.DocumentService.deleteDocumentById === "function") {
          await this.DocumentService.deleteDocumentById(document.document_id);
        } else {
          this.notifyError("Delete API method not found.");
          return;
        }

        this.notifySuccess("Document deleted successfully.");
        await this.fetchDocuments();
      } catch (error) {
        console.error("Error deleting document:", error);
        this.notifyError("Unable to delete document.");
      }
    },

    async handleAddComplete() {
      this.onAddPanelClose();
      await this.fetchDocuments();
    },

    async handleEditComplete() {
      this.onEditPanelClose();
      await this.fetchDocuments();
    },
  },
};
</script>

<style scoped>
::v-deep .column-h-text-left .p-column-header-content {
  text-align: left;
  display: block !important;
}

::v-deep .column-h-text-center .p-column-header-content {
  text-align: center;
  display: block !important;
}

::v-deep .column-h-text-right .p-column-header-content {
  text-align: right;
  display: block !important;
}
</style>
